import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, Copy, Heart, Mail, MessageSquare, Send } from "lucide-react";

import { fetchEventBySlug } from "@/lib/api/events";
import {
  fetchIncomingLikesCount,
  fetchLikedIds,
  fetchMatches,
  insertLike,
} from "@/lib/api/likes";
import {
  fetchEventFeed,
  type EventFeedRow,
  type ParticipantRow,
} from "@/lib/api/participants";
import type { MeetupMatchRow } from "@/lib/api/likes";
import { getParticipantSession } from "@/lib/participant-session";
import { formatCountdown, isRevealed, msUntilReveal } from "@/lib/reveal";
import { getMatchNote, setMatchNote } from "@/lib/match-notes";
import { buildIntroMessage, parseTelegramHandle, telegramDeepLink } from "@/lib/telegram";
import { copyToClipboard } from "@/lib/clipboard";

import { Card } from "@/components/ui/Card";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { Button, LinkButton } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Loader, InlineAlert } from "@/components/ui/States";
import { ParticipantShell } from "@/components/layout/ParticipantShell";

// Adapted shape — `other` is built from MeetupMatchRow returned by the RPC.
interface MatchRow {
  id: string;
  other: ParticipantRow;
}

/**
 * Adapt a raw RPC row into the `{ id, other: ParticipantRow }` shape this
 * page renders. Kept pure so we can call it in a `useMemo` and never store
 * the adapted form in the react-query cache — EventWrapPage and FeedPage
 * share the same `["matches", eventId, participantId]` cache key and expect
 * the raw shape, so writing the adapted shape there used to crash this page
 * with "Cannot read properties of undefined (reading 'role')" on every
 * client-side navigation that came in with a warm cache.
 */
function toMatchRow(m: MeetupMatchRow): MatchRow {
  return {
    id: m.match_id,
    other: {
      id: m.other_id,
      event_id: m.event_id,
      name: m.other_name,
      email: m.other_email ?? "",
      role: m.other_role,
      company: m.other_company,
      contact_for_matches: m.other_contact_for_matches,
      goals: null,
      looking_for: null,
      can_help_with: null,
      video_storage_path: m.other_video_storage_path,
      transcription: null,
      profile_json: m.other_profile_json,
      pitch_status: "approved",
      ai_flags: null,
      pitch_paei: null,
      pitch_paei_generated_at: null,
      pitch_analysis: null,
      pitch_analysis_generated_at: null,
      created_at: m.created_at,
    } satisfies ParticipantRow,
  };
}

const IMMINENT_MS = 60 * 60 * 1000; // last 60 min before reveal

export function MatchesPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const { data: event } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  // Store the raw MeetupMatchRow[] in the cache — the same shape every other
  // consumer (EventWrapPage, FeedPage) writes — and adapt to MatchRow[] in a
  // memo below. This is what unblocks the "View My Matches" click-through.
  const { data: rawMatches = [], isLoading } = useQuery<MeetupMatchRow[]>({
    queryKey: ["matches", session?.eventId, session?.participantId],
    queryFn: () =>
      fetchMatches(session?.eventId ?? "", session?.participantId ?? ""),
    enabled: Boolean(session?.eventId && session?.participantId),
  });

  const matches = useMemo<MatchRow[]>(
    () =>
      // Belt-and-braces: skip any rows missing the joined participant id —
      // shouldn't happen in practice, but a stale cache from a buggy older
      // build could leak through.
      rawMatches.filter((m) => m && m.other_id).map(toMatchRow),
    [rawMatches],
  );

  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);

  if (!session) return null;

  const revealed = event ? isRevealed(event, now) : false;
  const msToReveal = event ? msUntilReveal(event, now) : 0;
  const imminent = !revealed && msToReveal > 0 && msToReveal <= IMMINENT_MS;
  const countdown = event ? formatCountdown(msToReveal) : "--:--:--";

  return (
    <ParticipantShell matchesCount={matches.length}>
      <div className="animate-fade-in">
        {imminent ? (
          <ImminentRevealCountdown countdown={countdown} />
        ) : !revealed ? (
          <BeforeReveal countdown={countdown} revealHours={event?.match_reveal_hours} />
        ) : (
          <AfterReveal
            session={session}
            slug={slug ?? ""}
            matches={matches}
            isLoading={isLoading}
            eventTitle={event?.title ?? ""}
          />
        )}
      </div>
    </ParticipantShell>
  );
}

function BeforeReveal({
  countdown,
  revealHours,
}: {
  countdown: string;
  revealHours: number | undefined;
}) {
  return (
    <>
      <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Your matches</h1>
      <p className="mt-1 text-[13px] text-[#6c757d]">
        Matches reveal when the timer hits zero.
      </p>

      <Card tone="info" padding="lg" className="mt-5 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-white text-[#2563eb]">
          <Clock className="h-8 w-8" />
        </div>
        <p className="mt-4 text-[13px] text-[#475569]">Your matches will appear in</p>
        <div className="mt-1 text-[36px] font-bold tracking-tight text-[#2563eb]">{countdown}</div>
        <p className="mt-2 text-[12px] text-[#6c757d]">
          Reveal happens {revealHours}h before the event — everyone at once.
        </p>
      </Card>
      <InlineAlert tone="info" className="mt-3">
        A match appears here when <strong>both</strong> of you liked each other. At reveal, you'll
        see names, profiles, and contacts — for everyone simultaneously.
      </InlineAlert>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* D-1 — Imminent reveal countdown (last hour before reveal)           */
/* ------------------------------------------------------------------ */

function ImminentRevealCountdown({ countdown }: { countdown: string }) {
  return (
    <Card tone="info" padding="lg" className="mt-2 text-center">
      <p className="text-[12px] font-bold uppercase tracking-[1.2px] text-[#2563eb]">
        Matches reveal in
      </p>
      <div
        className="mt-2 text-[#1a1a1a] font-bold leading-none"
        style={{ fontSize: 56, letterSpacing: "-0.05em", fontVariantNumeric: "tabular-nums" }}
      >
        {countdown}
      </div>
      <p className="mt-1 text-[12px] text-[#6c757d]">hours · minutes · seconds</p>

      <Card padding="sm" className="mt-6 shadow-none border border-[#bfdbfe] bg-white text-left">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
          What's about to happen
        </p>
        <p className="mt-1 text-[13px] text-[#475569]">
          Names, profile photos, and contacts unlock at the same time for everyone — only on
          mutual likes.
        </p>
      </Card>

      <Card tone="flagged" padding="sm" className="mt-3 shadow-none text-left">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">Last call</p>
        <p className="mt-1 text-[13px] text-[#78350f]">
          Likes after reveal still work — but contacts won't unlock until both sides like.
        </p>
      </Card>

      <p className="mt-4 text-[11px] text-[#94a3b8]">
        Stay on this screen — we'll refresh automatically when matches go live.
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* After reveal — list of matches OR D-2 empty state                   */
/* ------------------------------------------------------------------ */

function AfterReveal({
  session,
  slug,
  matches,
  isLoading,
  eventTitle,
}: {
  session: { participantId: string; eventId: string; name: string };
  slug: string;
  matches: MatchRow[];
  isLoading: boolean;
  eventTitle: string;
}) {
  return (
    <>
      <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Your matches</h1>
      <p className="mt-1 text-[13px] text-[#6c757d]">
        {matches.length === 0
          ? "0 mutual connections"
          : `${matches.length} mutual connection${matches.length === 1 ? "" : "s"}`}
      </p>

      {isLoading ? <Loader /> : null}

      {!isLoading && matches.length === 0 ? (
        <ZeroMatchesAfterReveal session={session} slug={slug} />
      ) : null}

      {matches.length > 0 ? (
        <div className="mt-5 flex flex-col gap-3">
          {matches.map((m) => (
            <MatchCard
              key={m.id}
              matchId={m.id}
              participant={m.other}
              viewerEventId={session.eventId}
              viewerParticipantId={session.participantId}
              viewerName={session.name}
              eventTitle={eventTitle}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* D-2 — Zero matches after reveal: incoming-like count + similar goals */
/* ------------------------------------------------------------------ */

function ZeroMatchesAfterReveal({
  session,
  slug,
}: {
  session: { participantId: string; eventId: string };
  slug: string;
}) {
  const queryClient = useQueryClient();

  const { data: incomingLikes = 0 } = useQuery({
    queryKey: ["incoming-likes", session.eventId, session.participantId],
    queryFn: () => fetchIncomingLikesCount(session.eventId, session.participantId),
  });

  const { data: participants = [] } = useQuery({
    queryKey: ["event-feed", session.participantId],
    queryFn: () => fetchEventFeed(session.participantId),
  });

  const { data: likedIds = new Set<string>() } = useQuery({
    queryKey: ["liked-ids", session.eventId, session.participantId],
    queryFn: () => fetchLikedIds(session.eventId, session.participantId),
  });

  const me = participants.find((p) => p.id === session.participantId) ?? null;
  const similar = useMemo(
    () => pickSimilarGoals(me, participants, session.participantId, likedIds),
    [me, participants, session.participantId, likedIds],
  );

  const likeMutation = useMutation({
    mutationFn: (toId: string) =>
      insertLike(session.eventId, session.participantId, toId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["liked-ids", session.eventId, session.participantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["matches", session.eventId, session.participantId],
      });
    },
  });

  return (
    <>
      <Card padding="lg" className="mt-5 text-center">
        <div className="text-5xl opacity-60">🌱</div>
        <h3 className="mt-3 text-[18px] font-bold text-[#1a1a1a]">
          No mutual matches this time
        </h3>
        <p className="mt-2 text-[13px] text-[#6c757d]">
          Networking is a numbers game — most people get 1–2 mutuals per event. The folks below
          showed interest or share goals with you.
        </p>
      </Card>

      {incomingLikes > 0 ? (
        <Card tone="info" padding="md" className="mt-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#1d4ed8]">
            They liked you (no mutual)
          </p>
          <p className="mt-1 text-[14px] text-[#1d4ed8]">
            {incomingLikes} {incomingLikes === 1 ? "person" : "people"} liked you. Like them back
            from the feed to unlock a match.
          </p>
          <LinkButton
            to={`/e/${slug}/feed`}
            variant="secondary"
            size="sm"
            className="mt-3"
          >
            Open feed
          </LinkButton>
        </Card>
      ) : null}

      {similar.length > 0 ? (
        <div className="mt-5">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
            Similar goals
          </p>
          <div className="flex flex-col gap-3">
            {similar.map((p) => (
              <SimilarGoalsCard
                key={p.id}
                participant={p}
                liked={likedIds.has(p.id)}
                pending={
                  likeMutation.isPending && likeMutation.variables === p.id
                }
                onLike={() => likeMutation.mutate(p.id)}
                viewerEventId={session.eventId}
                viewerParticipantId={session.participantId}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

function pickSimilarGoals(
  me: EventFeedRow | null,
  all: EventFeedRow[],
  myId: string,
  likedIds: Set<string>,
): EventFeedRow[] {
  if (!me) return [];
  const myLooking = tokenize(me.looking_for ?? "");
  const myHelp = tokenize(me.can_help_with ?? "");
  if (myLooking.size === 0 && myHelp.size === 0) {
    return all
      .filter((p) => p.id !== myId && p.pitch_status === "approved" && !likedIds.has(p.id))
      .slice(0, 3);
  }

  const scored = all
    .filter((p) => p.id !== myId && p.pitch_status === "approved" && !likedIds.has(p.id))
    .map((p) => {
      const theirLooking = tokenize(p.looking_for ?? "");
      const theirHelp = tokenize(p.can_help_with ?? "");
      const score =
        intersectCount(myLooking, theirHelp) + intersectCount(myHelp, theirLooking);
      return { participant: p, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, 3).map((x) => x.participant);
}

const STOPWORDS = new Set([
  "the","a","an","and","or","but","for","to","of","in","on","with","at","by",
  "is","are","was","were","be","i","you","my","your","this","that","as","if","so",
  "can","will","not","no","any","from","up","out","want","need","looking","help",
]);

function tokenize(text: string): Set<string> {
  const out = new Set<string>();
  const matches = text.toLowerCase().match(/[a-zа-яё0-9][a-zа-яё0-9-]{2,}/gi) ?? [];
  for (const m of matches) if (!STOPWORDS.has(m)) out.add(m);
  return out;
}

function intersectCount(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const x of a) if (b.has(x)) n += 1;
  return n;
}

function SimilarGoalsCard({
  participant,
  liked,
  pending,
  onLike,
  viewerEventId,
  viewerParticipantId,
}: {
  participant: EventFeedRow;
  liked: boolean;
  pending: boolean;
  onLike: () => void;
  viewerEventId: string;
  viewerParticipantId: string;
}) {
  if (!participant) {
    if (typeof console !== "undefined") {
      console.error("[SimilarPersonCard] missing participant");
    }
    return null;
  }
  const roleLine = [participant.role, participant.company].filter(Boolean).join(" · ");
  const profile = participant.profile_json as
    | { headline?: string; about?: string }
    | null;
  const highlight =
    profile?.headline || profile?.about || participant.looking_for || participant.can_help_with;
  return (
    <Card hoverable padding="md">
      <div className="flex items-start gap-3">
        <VideoAvatar
          name={participant.name}
          size={56}
          bordered
          videoStoragePath={participant.video_storage_path}
          eventId={viewerEventId}
          viewerParticipantId={viewerParticipantId}
          showPlayBadge
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold text-[#1a1a1a]">{participant.name}</h3>
          {roleLine ? <p className="text-[12px] text-[#6c757d]">{roleLine}</p> : null}
        </div>
      </div>
      {highlight ? (
        <p className="mt-3 rounded-[10px] bg-[#f8fafc] p-3 text-[13px] text-[#475569]">
          {highlight}
        </p>
      ) : null}
      <Button
        onClick={onLike}
        disabled={liked || pending}
        loading={pending}
        leadingIcon={<Heart className="h-4 w-4" fill={liked ? "currentColor" : "none"} />}
        fullWidth
        size="md"
        className={liked ? "mt-3 bg-[#f0fdf4] text-[#065f46] hover:bg-[#f0fdf4]" : "mt-3"}
      >
        {liked ? "Liked" : "Like"}
      </Button>
    </Card>
  );
}

function MatchCard({
  matchId,
  participant,
  viewerEventId,
  viewerParticipantId,
  viewerName,
  eventTitle,
}: {
  matchId: string;
  participant: ParticipantRow;
  viewerEventId: string;
  viewerParticipantId: string;
  viewerName: string;
  eventTitle: string;
}) {
  const [note, setNote] = useState<string>(() => getMatchNote(matchId));
  const [copied, setCopied] = useState(false);
  // Defensive: in production we saw `participant` come through as undefined
  // even though the type says otherwise. Log once so we can chase the data
  // source, and render a quiet fallback instead of crashing the whole route.
  if (!participant) {
    if (typeof console !== "undefined") {
      console.error("[MatchCard] missing participant", { matchId });
    }
    return null;
  }
  const roleLine = [participant.role, participant.company].filter(Boolean).join(" · ");
  const profile = participant.profile_json as
    | { headline?: string; looking_for?: string; can_help_with?: string }
    | null;

  // Detect a Telegram handle inside the free-form contact_for_matches
  // string. Falls back to plain selectable text when nothing matches.
  const telegramHandle = parseTelegramHandle(participant.contact_for_matches);
  const myProfileUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/p/${viewerParticipantId}`
      : `https://meetup.finally.plus/p/${viewerParticipantId}`;
  const introMessage = buildIntroMessage({
    myName: viewerName,
    otherName: participant.name,
    eventTitle,
    profileUrl: myProfileUrl,
  });

  const handleCopy = async () => {
    const ok = await copyToClipboard(introMessage);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card padding="md">
      <div className="flex items-start gap-3">
        <VideoAvatar
          name={participant.name}
          size={56}
          bordered
          videoStoragePath={participant.video_storage_path}
          eventId={viewerEventId}
          viewerParticipantId={viewerParticipantId}
          showPlayBadge
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-[16px] font-bold text-[#1a1a1a]">{participant.name}</h3>
          {roleLine ? <p className="text-[12px] text-[#6c757d]">{roleLine}</p> : null}
          {profile?.headline ? (
            <p className="mt-1 text-[13px] text-[#475569]">{profile.headline}</p>
          ) : null}
        </div>
      </div>

      {(profile?.looking_for || participant.looking_for) ? (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
            Looking for
          </p>
          <p className="mt-1 text-[13px] text-[#475569]">
            {profile?.looking_for || participant.looking_for}
          </p>
        </div>
      ) : null}

      {(profile?.can_help_with || participant.can_help_with) ? (
        <div className="mt-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
            Can help with
          </p>
          <p className="mt-1 text-[13px] text-[#475569]">
            {profile?.can_help_with || participant.can_help_with}
          </p>
        </div>
      ) : null}

      <div className="mt-4 rounded-[10px] bg-[#f0fdf4] p-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#065f46]">Contact</p>

        {participant.email ? (
          <a
            href={`mailto:${participant.email}`}
            className="mt-1 flex items-center gap-1.5 text-[13px] text-[#1a1a1a] no-underline hover:underline"
          >
            <Mail className="h-3.5 w-3.5 shrink-0 text-[#065f46]" />
            <span className="break-all">{participant.email}</span>
          </a>
        ) : null}

        {participant.contact_for_matches ? (
          <p className="mt-1 flex items-center gap-1.5 select-all text-[13px] text-[#1a1a1a]">
            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-[#065f46]" />
            <span className="break-all">{participant.contact_for_matches}</span>
          </p>
        ) : null}

        {telegramHandle ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <a
              href={telegramDeepLink(telegramHandle)}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#2563eb] px-4 py-2.5 text-[14px] font-semibold text-white no-underline shadow-sm transition-colors hover:bg-[#1d4ed8]"
            >
              <Send className="h-4 w-4" />
              Message in Telegram
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-[#bfdbfe] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#2563eb] transition-colors hover:bg-[#eff6ff]"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy intro
                </>
              )}
            </button>
          </div>
        ) : null}

        {telegramHandle ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-[11px] font-semibold text-[#475569] hover:text-[#1a1a1a]">
              Preview message
            </summary>
            <pre className="mt-2 whitespace-pre-wrap rounded-[8px] bg-white p-2.5 text-[12px] leading-relaxed text-[#475569]">
              {introMessage}
            </pre>
          </details>
        ) : null}
      </div>

      <div className="mt-4">
        <Field label="Personal note (stored on this device)">
          <Input
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setMatchNote(matchId, e.target.value);
            }}
            placeholder="What do you want to discuss?"
          />
        </Field>
      </div>
    </Card>
  );
}
